import React, { useEffect } from "react"
import headshot from 'images/blog/avatar-100.jpg'
import RequestDemoModal from "../../../pages/common/RequestDemoModal"
import ReceiveUpdatesModal from "../../../pages/common/ReceiveUpdatesModal"
import { Link } from "react-router-dom"
import BlogSignupSection from "../../common/BlogSignupSection"

import BannerImage500 from 'images/blog/Blog_20260707_HedgedEquities/banner.png'
import chart1Scatter from 'images/blog/Blog_20260707_HedgedEquities/chart1_scatter.png'
import chart2Payoff from 'images/blog/Blog_20260707_HedgedEquities/chart2_payoff.png'
import chart3Waterfall from 'images/blog/Blog_20260707_HedgedEquities/chart3_waterfall.png'

import jonAvatar from 'images/blog/avatar-100.jpg'

const documentTitle = "Clarivor Blog"
const postTitle = 'Hedging to Own More Stocks'
const postSubtitle = (
    <React.Fragment>
        Downside puts can let cautious investors<br />
        hold more equities and fewer bonds
    </React.Fragment>
)
const postAuthor = 'Jon Spiegel, CFA'
const publishedOnText = 'July 9, 2026'
const topic = 'hedging'
export const path = 'hedging-to-own-more-stocks-20260707'

const HeroSection = (props) => (
    <section
        className='bg-dark text-white'
        style={{
            position: 'relative',
            zIndex: 0,
            minHeight: '35vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            backgroundColor: '#f0f0f0',
            color: '#000',
            padding: '20px'
        }}>

        <div className='container bg-dark text-white'>
            <div className={'row justify-content-center'}>
                <h1 style={{
                    fontSize: '3.0em',
                    fontWeight: 'bold',
                    textAlign: 'center'
                }}>
                    {postTitle}
                </h1>
                <div className="px-md-12 mt-6">
                    <h2 style={{
                        fontSize: '1.5em',
                        textAlign: 'center'
                    }}>
                        {postSubtitle}
                    </h2>
                    <p style={{
                        fontSize: '1em',
                        textAlign: 'center'
                    }}>
                        {publishedOnText}
                    </p>
                </div>
            </div>
        </div>

    </section>)

function Summary(props) {
    return (
        <React.Fragment>
            <h3 className={'font-weight-bold mt-6'}>
                Summary
            </h3>
            <ul>
                <li className='my-2'>
                    For investors who want more growth in their portfolios but cannot afford the risk of a large equity sell-off,
                    pairing a higher equity allocation with protective puts can target downside risk similar to a traditional
                    portfolio, with significantly more exposure to equity upside.
                </li>
                <li className='my-2'>
                    Replacing bonds with equities and puts also reduces reliance on bonds as a source of diversification,
                    a cushion that has been less dependable in inflationary periods, most recently in 2022.
                </li>
                <li className='my-2'>
                    When the puts are struck well below the market, investors pay only to insure against the losses they
                    can't tolerate, while accepting the risk of moderate sell-offs.
                </li>
            </ul>
        </React.Fragment>
    )
}

function Section1(props) {
    return (
        <React.Fragment>
            <h3 className={'font-weight-bold mt-6'}>
                Using Puts to Hold More Equities
            </h3>
            <p>
                Cautious investors traditionally dampen portfolio risk with large bond allocations. The
                implicit trade-off is upside: the bonds in a 60-40 portfolio can create a significant drag
                on performance when equities rally.
            </p>

            <p>
                Rather than relying primarily on bonds to reduce risk, investors can hold more equities and
                hedge the tail with a put option. By limiting severe downside exposure, the put can make a
                larger equity allocation more tolerable for cautious investors, while preserving more
                exposure to equity upside.
            </p>

            <p>
                A put option on the SPY ETF provides protection if the ETF falls below the option's strike
                price. When an investor's equity allocation has a similar risk profile to the S&P 500, the
                put can be an effective hedge against severe equity market declines.
            </p>

            <p>
                Out-of-the-money puts with longer maturities can make this protection more attractive.
                With SPY at $751, an investor can currently buy roughly two years of protection beginning
                25% below the market for approximately $20, about 2.7% of the protected value, or about
                1.35% per year.<sup><i>1</i></sup> Because listed strikes and expirations don't match those
                terms exactly, the cost is estimated from nearby listed SPY put options and includes an
                allowance for the bid-ask spread. The cost of this level of protection is currently lower
                than it has been about two-thirds of the time since 2005.
            </p>
            <p>
                This is tail-risk hedging: the put does not protect against the first 25% of losses,
                but it does create a contractual floor on the hedged SPY exposure, protecting
                against the severe declines that can disrupt an investor’s financial plan.
            </p>
            <p>
                This structure may suit clients who want to manage downside risk but are reluctant to give up
                potential gains in exchange.
            </p>
            <div className='card mx-auto my-6'>
                <div className='card-body p-2 bg-white text-center'>
                    <h3 className='text-center mb-1'>
                        Scenario With a Put Overlay
                    </h3>
                    <p className='text-center text-muted mb-3'>
                        2yr Hedged 80-20 vs. traditional 60-40
                    </p>
                    <img src={chart2Payoff} className='img-fluid'
                        alt='Payoff at expiration: hedged 80-20 vs traditional 60-40 across SPY returns' />
                    <p className='text-center mt-3' style={{ fontSize: '0.8rem' }}>
                        Portfolio return over the put's 2-year life, bonds flat, put premium included. Put pricing
                        as of July 6, 2026.<sup><i>2</i></sup>
                    </p>
                </div>
            </div>
        </React.Fragment>
    )
}

function ScenarioTable(props) {
    const tdClassName = 'text-center py-2'
    const thClassName = 'text-center'
    const subRowStyle = { paddingLeft: '1.5rem', fontStyle: 'italic' }

    return (
        <div className='row justify-content-center'>
            <div className='col-12 col-md-11 col-lg-10'>
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th scope="col">Scenario (over 2 years)</th>
                            <th scope="col" className={thClassName}>60-40</th>
                            <th scope="col" className={thClassName}>80-20 + put</th>
                            <th scope="col" className={thClassName}>Difference</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Expected annual return (long-run averages, net of hedge cost)</td>
                            <td className={tdClassName}>7.82%</td>
                            <td className={tdClassName}>7.84%</td>
                            <td className={tdClassName}>+0.02</td>
                        </tr>
                        <tr>
                            <td>SPY &minus;35%, bonds flat</td>
                            <td className={tdClassName}>&minus;21.0%</td>
                            <td className={tdClassName}>&minus;22.2%</td>
                            <td className={tdClassName}>&minus;1.2</td>
                        </tr>
                        <tr>
                            <td style={subRowStyle}>&hellip;bonds +5.2% (as in 2008)</td>
                            <td className={tdClassName}>&minus;18.9%</td>
                            <td className={tdClassName}>&minus;21.1%</td>
                            <td className={tdClassName}>&minus;2.2</td>
                        </tr>
                        <tr>
                            <td style={subRowStyle}>&hellip;bonds &minus;13.0% (as in 2022)</td>
                            <td className={tdClassName}>&minus;26.2%</td>
                            <td className={tdClassName}>&minus;24.8%</td>
                            <td className={tdClassName}>+1.4</td>
                        </tr>
                        <tr>
                            <td>SPY &minus;50%, bonds flat</td>
                            <td className={tdClassName}>&minus;30.0%</td>
                            <td className={tdClassName}>&minus;22.2%</td>
                            <td className={tdClassName}>+7.8</td>
                        </tr>
                        <tr>
                            <td style={subRowStyle}>&hellip;bonds +5.2% (as in 2008)</td>
                            <td className={tdClassName}>&minus;27.9%</td>
                            <td className={tdClassName}>&minus;21.1%</td>
                            <td className={tdClassName}>+6.8</td>
                        </tr>
                        <tr>
                            <td style={subRowStyle}>&hellip;bonds &minus;13.0% (as in 2022)</td>
                            <td className={tdClassName}>&minus;35.2%</td>
                            <td className={tdClassName}>&minus;24.8%</td>
                            <td className={tdClassName}>+10.4</td>
                        </tr>
                        <tr>
                            <td>SPY +20%, bonds flat</td>
                            <td className={tdClassName}>+12.0%</td>
                            <td className={tdClassName}>+13.8%</td>
                            <td className={tdClassName}>+1.8</td>
                        </tr>
                        <tr>
                            <td>SPY +30%, bonds flat</td>
                            <td className={tdClassName}>+18.0%</td>
                            <td className={tdClassName}>+21.8%</td>
                            <td className={tdClassName}>+3.8</td>
                        </tr>
                    </tbody>
                </table>
                <p className='text-center text-muted' style={{ fontSize: '0.8rem' }}>
                    The put overlay covers the full equity allocation (a 2-year 25% OTM SPY put, premium included,
                    held to expiration), priced as of July 6, 2026. Difference is the hedged 80-20 return minus
                    the 60-40 return, in percentage points.<sup><i>2, 6, 7</i></sup>
                </p>
            </div>
        </div>
    )
}

function Section2(props) {
    return (
        <React.Fragment>
            <h3 className={'font-weight-bold mt-6'}>
                The Risk That Bonds Sell Off Too
            </h3>
            <p>
                The case for holding fewer bonds is about more than upside. The traditional portfolio's defense
                rests on an assumption: that when equities fall sharply, bonds hold their value, or rally.
            </p>
            <p>
                That assumption held for most of the 2000s and 2010s, but it is a statistical relationship rather
                than a guarantee, and in inflationary regimes it has tended to break down.
            </p>
            <p>
                In 2022, the S&amp;P 500 fell approximately 18% while the aggregate bond market fell approximately
                13%. The asset that was expected to cushion the portfolio instead deepened the
                drawdown.<sup><i>3</i></sup>
            </p>
            <p>
                The 1970s tell a similar story. Over the seven quarters of the 1973&ndash;74 bear market, US
                stocks fell approximately 47% while 10-year Treasuries returned approximately +2%, providing no cushion in
                nominal terms, and with inflation running double digits, a meaningful loss in real
                terms.<sup><i>4</i></sup>
            </p>

            <div className='card mx-auto my-6'>
                <div className='card-body p-2 bg-white text-center'>
                    <h3 className='text-center mb-1'>
                        When Stocks Fall, Do Bonds Rally?
                    </h3>
                    <p className='text-center text-muted mb-3'>
                        Quarterly returns, 1962&ndash;Q1 2026
                    </p>
                    <img src={chart1Scatter} className='img-fluid'
                        alt='Scatter of quarterly US stock returns vs 10-year Treasury returns since 1962' />
                    <p className='text-center mt-3' style={{ fontSize: '0.8rem' }}>
                        Quarterly total returns, 1962&ndash;2026. Treasury returns estimated from FRED 10-year
                        constant-maturity yields; equity returns from Ken French's data library (CRSP US total
                        market).
                    </p>
                </div>
            </div>

            <p>
                Investors who believe inflation risk remains in the background may question how much protection
                their bond allocation actually provides in the scenarios they care about most. A put option
                doesn't depend on that relationship: it pays when equities fall, regardless of what inflation
                and interest rates are doing.
            </p>
        </React.Fragment>
    )
}

function Section3(props) {
    return (
        <React.Fragment>
            <h3 className={'font-weight-bold mt-6'}>
                The Arithmetic of Owning More Equities
            </h3>
            <p>
                Based on U.S. equity and bond returns since 1928, the expected return pickup from owning more
                equities can pay for the put protection, while preserving more upside participation if stocks rally.
            </p>
            <p>
                Since 1928, equities have returned approximately 10% annually, versus approximately 4.5%
                for 10-year Treasuries.<sup><i>5</i></sup> Each 10% of the portfolio moved from bonds to equities
                has therefore added roughly half a percentage point of expected annual return.
            </p>
            <p>
                Consider an investor moving from a traditional 60-40 to an 80-20 portfolio with a 25% OTM put
                protecting the equity allocation. The added equity exposure picks up approximately 1.1 percentage
                points of expected annual return, while the protection costs approximately 1.1 percentage points
                per year.<sup><i>6</i></sup>
            </p>
            <p>
                In an average year, the two roughly offset, and at today's put prices, the pickup slightly more
                than offsets the hedge cost.
                What separates the two portfolios is a large equity move in either direction.
            </p>

            <div className='card mx-auto my-6'>
                <div className='card-body p-2 bg-white text-center'>
                    <h3 className='text-center mb-1'>
                        The Pickup Pays for the Put
                    </h3>
                    <p className='text-center text-muted mb-3'>
                        Expected annual return, 60-40 &rarr; hedged 80-20
                    </p>
                    <img src={chart3Waterfall} className='img-fluid'
                        alt='Waterfall: 60-40 expected return plus equity pickup minus put cost equals hedged 80-20 expected return' />
                    <p className='text-center mt-3' style={{ fontSize: '0.8rem' }}>
                        Long-run averages, 1928&ndash;2025; put cost annualized over the 2-year
                        term.<sup><i>5, 6</i></sup> Steps shown to two decimals so the arithmetic ties out;
                        the same endpoints appear in the scenario table below.
                    </p>
                </div>
            </div>

            <p>
                On the downside, the SPY put creates a floor for the protected market exposure if the equity
                allocation tracks the S&amp;P 500. The next section shows how the floor affects portfolio outcomes under
                the stated assumptions. In a rally, the extra exposure compounds: if equities gained 20% over the period,
                a traditional 60-40 portfolio would return approximately +12%, while the hedged 80-20 portfolio
                would return approximately +14%.<sup><i>2</i></sup> Unlike capped structures, the gap also widens
                as the rally extends: at a 30% gain, approximately +18% for the 60-40 versus approximately +22%
                for the hedged 80-20.
            </p>
        </React.Fragment>
    )
}

function Section4(props) {
    return (
        <React.Fragment>
            <h3 className={'font-weight-bold mt-6'}>
                A Similar Downside Budget, With a Floor
            </h3>
            <p>
                If equities lost 35% over the 2-year period, a traditional 60-40 portfolio would return approximately
                &minus;21%, and the hedged 80-20 portfolio approximately &minus;22%, a similar loss, with the
                important difference that the hedged portfolio's loss does not deepen further no matter how far
                equities fall.<sup><i>2</i></sup>
            </p>
            <p>
                At a 50% equity decline, the 60-40 loses approximately 30%; the hedged 80-20, still approximately
                22%.
            </p>
            <p>
                Both figures assume bonds are flat over the period. That assumption matters twice as much for
                the 60-40, which holds twice the bonds — and, as the 2022 and 1973&ndash;74 episodes showed, it
                is not necessarily a safe one.
            </p>

            <ScenarioTable />

            <p>
                The italicized rows in the table show how much the bond assumption matters. If bonds behaved as
                they did in 2008, up 5% while equities crashed, the 60-40's loss in the &minus;35% scenario
                improves to approximately &minus;19%. If they behaved as they did in 2022, down 13% alongside
                stocks, it deepens to approximately &minus;26%.<sup><i>7</i></sup>
            </p>
            <p>
                The hedged 80-20 lands between roughly &minus;21% and &minus;25% in the same two cases, and
                stays there even at a 50% equity decline, because the put's floor is already binding. The 60-40
                has no floor: in the &minus;50% scenario its loss ranges from roughly &minus;28% to &minus;35%
                depending on what bonds do. Its protection is statistical; the hedged portfolio's is contractual.
            </p>
        </React.Fragment>
    )
}

function Section5(props) {
    return (
        <React.Fragment>
            <h3 className={'font-weight-bold mt-6'}>
                Choosing What to Give Up
            </h3>
            <p>
                Advisors already have several ways to manage the cost of protection. Covered calls and buffer
                ETFs do it by selling upside — a <Link to={'/blog/BufferBondReplacement_20210624'}>previous
                    post discussed buffers as partial bond replacements</Link>.
            </p>
            <p>
                Those structures pay for income or protection by selling upside. For an income-oriented client,
                that is a deliberate and often sensible trade.
            </p>
            <p>
                A put overlay makes the opposite trade: it pays a visible premium and keeps the upside, which
                matters most for the client who moved to a higher equity allocation to capture more of it.
            </p>
            <p>
                For clients who want protection against more moderate sell-offs and also want to manage the cost,
                a put spread can be a useful middle ground. Selling a lower-strike put helps fund the protection,
                but it also caps the hedge: if the market falls through both strikes, the tail is no longer protected.
            </p>
            <p>
                The right structure depends on what the client is solving for. None of these approaches is free,
                and none fits every client. Each one pays for protection differently, and some costs are easier
                to see than others.
            </p>

            <h3 className={'font-weight-bold mt-6'}>
                What It Costs, and When
            </h3>
            <p>
                This series focuses on the client who wants to stay fully invested and keep tail protection;
                income-focused and put-spread structures deserve their own treatment.
            </p>
            <p>
                The most common objection to tail hedging is the cost, which is real, varies significantly over
                time, and depends heavily on the maturity of the options used.
            </p>
            <p>
                In the next post, we look at what rolling out-of-the-money SPY protection has actually cost since
                2005 across 3-month, 6-month, 12-month, and 24-month maturities, and why longer-dated protection
                has typically been the cheaper way to stay hedged.
            </p>
        </React.Fragment>
    )
}

function Footnotes(props) {
    return (
        <div className='font-italic text-muted mt-6' style={{ fontSize: '0.9rem' }}>
            <p>
                <sup>1</sup> As of July 6, 2026: SPY at $751.28. The hedge is modeled as a 24-month put struck
                at 75% of spot (approximately $563). SPY options are not listed at exactly that strike or
                expiration, so the cost is interpolated across listed strikes and expirations: approximately
                2.65% of spot at midpoint prices, quoted here as 2.7% (approximately $20, about 1.35% per year)
                to allow for the bid-ask spread. All scenario calculations use the 2.7% figure. On historical
                dates when no listed expiration extended a full 24 months, pricing uses the longest available.
                The cost comparison uses daily observations of the same interpolated 24-month 25% OTM put
                premium since January 2005, on a consistent midpoint basis.
            </p>
            <p>
                <sup>2</sup> Scenario calculations assume bonds have zero return over the period and include the
                put premium and assume the equity allocation tracks SPY. 60-40: 0.6 &times; &minus;35% = &minus;21.0%.
                Hedged 80-20: 0.8 &times; (&minus;35% + 10% put payoff &minus; 2.7% premium) &asymp; &minus;22.2%.
                No dividends or interest income are modeled on either side.
            </p>
            <p>
                <sup>3</sup> 2022 calendar-year total returns: S&amp;P 500 &minus;18.1%; Bloomberg US Aggregate
                bond index &minus;13.0%.
            </p>
            <p>
                <sup>4</sup> Q1 1973 through Q3 1974: CRSP US total market &minus;46.5%; 10-year US Treasury
                total return +1.6%, estimated from FRED constant-maturity yields (the same series as the chart).
                US consumer prices rose approximately 12% over the twelve months ending December 1974 (BLS CPI-U).
            </p>
            <p>
                <sup>5</sup> Long-run geometric average annual returns, 1928&ndash;2025: S&amp;P 500 10.0%;
                10-year US Treasuries 4.5%. Source: Damodaran, NYU Stern, "Historical Returns on Stocks, Bonds
                and Bills."
            </p>
            <p>
                <sup>6</sup> Hedge cost estimated as the 2-year 25% OTM put premium (2.7% of protected notional
                including bid-ask allowance, about 1.35% per year), applied to the 80% equity allocation:
                approximately 1.1% of portfolio
                value per year. The put is sized to the full equity allocation and held to expiration.
            </p>
            <p>
                <sup>7</sup> Bond scenario returns are the realized calendar-year total returns of the Bloomberg
                US Aggregate in the two episodes, not model outputs: 2008 +5.2% (while the S&amp;P 500 fell
                37.0%); 2022 &minus;13.0% (while the S&amp;P 500 fell 18.1%). In the SPY &minus;35% scenario,
                60-40 with bonds +5.2%: &asymp; &minus;18.9%; with bonds &minus;13.0%: &asymp; &minus;26.2%. In
                the SPY &minus;50% scenario: &asymp; &minus;27.9% and &minus;35.2%. The hedged 80-20 returns
                &asymp; &minus;21.1% (bonds +5.2%) and &minus;24.8% (bonds &minus;13.0%) in both equity
                scenarios, because the put's floor is binding at either decline.
            </p>
        </div>
    )
}

function DisclosuresLink(props) {
    return (
        <a data-toggle="modal" href="#disclaimerModal"
            onClick={() => null}>Disclosures</a>
    )
}

function TermsOfUseLink(props) {
    return (
        <Link to={'/terms-of-use'} target={'_blank'}
            onClick={() => null}>Terms of Use</Link>
    )
}

function SourceDisclaimer(props) {
    return (
        <div>
            <hr />
            <p className='font-italic text-muted'>
                Source: Clarivor LLC.<br />
                Intended for professional investors; options can entail complex risks and may not be suitable
                for particular situations. Hypothetical scenarios are illustrations based on stated assumptions,
                not predictions or backtested performance.<br />
                Nothing in this post should be construed as investment advice.<br />
                <DisclosuresLink /> and <TermsOfUseLink /> apply.<br />
            </p>
        </div>
    )
}

function PostBody(props) {
    return (
        <React.Fragment>
            <section className="pt-0 pt-md-0">
                <ReceiveUpdatesModal />
                <RequestDemoModal />
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-12 col-lg-10 col-xl-9">
                            <Summary />
                            <Section1 />
                            <Section2 />
                            <Section3 />
                            <Section4 />
                            <Section5 />
                            <Footnotes />
                            <SourceDisclaimer />
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    )
}

export default function Blog_20260707_HedgedEquities(props) {
    useEffect(() => {
        document.title = documentTitle
    }, [])

    return (
        <React.Fragment>
            <HeroSection />
            <PostBody />
            <BlogSignupSection fromLocation={'Blog Post Hedged Equities'} />
        </React.Fragment>
    )
}

export function RichHedgedEquities20260707(props) {
    return (
        <section className="pt-2 pt-md-4">
            <div className="container">
                <div className="row">
                    <div className="col-12">

                        <div className="card card-row shadow-light-lg mb-6 lift lift-lg">
                            <div className="row no-gutters">
                                <div className="col-12">

                                    <span className="badge badge-pill badge-light badge-float badge-float-inside">
                                        <span className="h6 text-uppercase">{topic}</span>
                                    </span>
                                </div>
                                <Link className="col-12 col-md-6 order-md-2 bg-cover card-img-right"
                                    style={{ backgroundImage: "url(" + BannerImage500 + ")" }}
                                    to={props.blogPath + path}>

                                    <img src="assets/img/photos/photo-27.jpg" alt="..."
                                        className="img-fluid d-md-none invisible" />

                                    <div
                                        className="shape shape-left shape-fluid-y svg-shim text-white d-none d-md-block">
                                        <svg viewBox="0 0 112 690" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0 0h62.759v172C38.62 384 112 517 112 517v173H0V0z"
                                                fill="currentColor"></path>
                                        </svg>
                                    </div>

                                </Link>
                                <div className="col-12 col-md-6 order-md-1">

                                    <Link className="card-body" to={props.blogPath + path}>

                                        <h2>
                                            {postTitle}
                                        </h2>

                                        <p className="text-muted">
                                            {postSubtitle}
                                        </p>

                                        <ul className="text-muted mb-0 pl-4">
                                            <li className="my-2">
                                                Pairing a higher equity allocation with protective puts can target
                                                downside risk similar to a traditional portfolio, with 80%+
                                                exposure to equity upside.
                                            </li>
                                            <li className="my-2">
                                                Replacing bonds with equities and puts reduces reliance on bonds as
                                                a diversifier, a cushion that has been less dependable in
                                                inflationary periods, most recently in 2022.
                                            </li>
                                            <li className="my-2">
                                                Puts struck well below the market insure only against the losses
                                                investors can't tolerate, keeping the cost of protection down.
                                            </li>
                                        </ul>

                                    </Link>

                                    <a className="card-meta" href="#!">

                                        <hr className="card-meta-divider" />

                                        <div className="avatar avatar-sm mr-2">
                                            <img src={jonAvatar} alt="..."
                                                className="avatar-img rounded-circle" />
                                        </div>

                                        <h6 className="text-uppercase text-muted mr-2 mb-0">
                                            Jon Spiegel
                                        </h6>

                                        <p className="h6 text-uppercase text-muted mb-0 ml-auto">
                                            <time dateTime="2026-07-09">{publishedOnText}</time>
                                        </p>

                                    </a>

                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>

    )
}
